import React, { useCallback, useEffect, useMemo, } from 'react';
import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import { AtlasForm, IForm, IFormOptions, } from 'atlas-form';
import {
  DispositionEditForm,
  DispositionEditInitialValues,
  DispositionComponents,
  DispositionAdditionalParams,
  DispositionFormOptions,
  onDispositionFormSaved,
  IDispositionFormOptionsProps,
  AlertError,
} from '@Utils';
import { ISharedDisposition, ISharedPatchDisposition, PermissionEnum, CrudEnum, } from 'atlas-shared';
import { fetchDisposition, patchDisposition, getDispositionUpdateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withEdit, IWithEditProps, } from '@Hocs/with.edit';
import { useAbilities, useAuth, useOrganizations, useForms, useDispositions, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithEditProps<ISharedDisposition> {}

const DispositionFormEdit = ({ id, value, setValue, formSchema, schemaLoading, saving, save, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const abilities = useAbilities();
  const dispatch = useAppDispatch();
  const organizations = useOrganizations();
  const forms = useForms();
  const dispositions = useDispositions();

  useEffect(() => {
    fetchDisposition(id).then(disposition => setValue(disposition));
  }, [id, setValue, ]);

  const param_props: IDispositionFormOptionsProps = {
    t,
    is_new: false,
    abilities,
    organizations: organizations.organizations,
    forms: forms.forms,
    dispositions: dispositions.dispositions,
  };
  const formLayout: IForm | undefined = useMemo(() => value && DispositionEditForm(value, param_props, t), [value, organizations, forms, dispositions, t, abilities, ]);
  const options: IFormOptions | undefined = useMemo(() => value && DispositionFormOptions(value, param_props, t), [value, organizations, forms, dispositions, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedPatchDisposition, form) => {
    onChange?.(values, changedValue, form);
  }, []);
  const onFinish = useCallback((values: ISharedPatchDisposition) => value && save(() => patchDisposition(value.id, values).then(
    res => onSaved ? onSaved(res, navigate) : onDispositionFormSaved(t, 'edit', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [value, save, navigate, t, ]);
  
  if (schemaLoading)
    return <AppSpinner />;

  if (!value)
    return <>{t('ITEM_NOT_FOUND')}</>;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!organizations.loaded || !forms.loaded || !dispositions.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !value || abilities.can(value.organization_id, CrudEnum.Update, PermissionEnum.Disposition, value)}
    >
      <AtlasForm<ISharedPatchDisposition>
        dispatch={dispatch}
        formLayout={formLayout as IForm}
        form={formSchema}
        options={options}
        components={DispositionComponents}
        additionalParams={DispositionAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={DispositionEditInitialValues(value)}
        saving={saving}
        title={title ? t(title) : `${t('EDIT')} ${t('DISPOSITION')}`}
        entityId={value.id}
        entity={value}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const DispositionFormEditView = React.memo(withEdit(DispositionFormEdit, getDispositionUpdateSchema));
